import React, { useState, useEffect } from 'react';
import { X, User, Mail, Phone, Calendar, MapPin, CreditCard, Users, TrendingUp, Eye, MessageSquare, Shield, AlertTriangle, CheckCircle, Crown } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';

const MemberCRMModal = ({ member, onClose }) => {
  const [memberDetails, setMemberDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (member) {
      fetchMemberDetails();
    }
  }, [member]);

  const fetchMemberDetails = async () => {
    try {
      // This would be an actual API call in production
      // For now, simulate detailed member data
      const mockDetails = {
        ...member,
        phone: member.phone || '+1 (555) 123-4567',
        location: 'United States',
        timezone: 'EST',
        registration_date: member.created_at || new Date().toISOString(),
        last_login: member.last_login || new Date().toISOString(),
        total_earnings: 285.50,
        total_referrals: 7,
        pif_sponsored: 3,
        lead_capture_pages: 2,
        active_campaigns: 1,
        tron_wallet: member.tron_wallet_address || 'Not provided',
        network_activity: [
          { type: 'referral', description: 'Referred new member: Sarah Johnson', date: '2024-10-01' },
          { type: 'pif', description: 'Paid it forward to John Doe', date: '2024-09-28' },
          { type: 'login', description: 'Last login from Chrome', date: '2024-10-02' }
        ],
        compliance_notes: [],
        support_tickets: [
          { id: 'T001', subject: 'Password reset help', status: 'resolved', date: '2024-09-25' },
          { id: 'T002', subject: 'Commission question', status: 'open', date: '2024-10-01' }
        ]
      };
      
      setMemberDetails(mockDetails);
    } catch (error) {
      toast.error('Failed to load member details');
    } finally {
      setLoading(false);
    }
  };

  const getMembershipTierInfo = (tier) => {
    const tiers = {
      'free': { label: 'Boot Camp (Free)', color: 'bg-gray-100 text-gray-800', icon: User },
      'basic': { label: 'Rookie', color: 'bg-blue-100 text-blue-800', icon: Crown },
      'rookie_pro': { label: 'Rookie Pro', color: 'bg-green-100 text-green-800', icon: Crown },
      'pro': { label: 'Pro', color: 'bg-purple-100 text-purple-800', icon: Crown },
      'enterprise': { label: 'Enterprise', color: 'bg-orange-100 text-orange-800', icon: Crown }
    };
    return tiers[tier] || tiers.free;
  };

  const getStatusColor = (status) => {
    const colors = {
      'active': 'text-green-600',
      'inactive': 'text-yellow-600', 
      'suspended': 'text-red-600'
    };
    return colors[status] || 'text-gray-600';
  };

  if (!member) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center font-bold text-black text-lg">
                {member.full_name?.charAt(0) || 'U'}
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {member.full_name || 'Unknown User'}
                </h2>
                <div className="flex items-center space-x-3 mt-1">
                  <span className="text-gray-600">{member.email}</span>
                  <Badge className={getMembershipTierInfo(member.membership_tier || 'free').color}>
                    {React.createElement(getMembershipTierInfo(member.membership_tier || 'free').icon, { className: "h-3 w-3 mr-1" })}
                    {getMembershipTierInfo(member.membership_tier || 'free').label}
                  </Badge>
                  <Badge className={`${getStatusColor(member.status)} bg-opacity-10`}>
                    {member.status || 'active'}
                  </Badge>
                </div>
              </div>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-6 w-6" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
            </div>
          ) : (
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="financial">Financial</TabsTrigger>
                <TabsTrigger value="activity">Activity</TabsTrigger>
                <TabsTrigger value="support">Support</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Personal Information */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <User className="h-5 w-5" />
                        Personal Information
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Full Name:</span>
                        <span className="font-medium">{memberDetails?.full_name || 'Not provided'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Email:</span>
                        <span className="font-medium">{memberDetails?.email}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Phone:</span>
                        <span className="font-medium">{memberDetails?.phone || 'Not provided'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Location:</span>
                        <span className="font-medium">{memberDetails?.location || 'Not provided'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Timezone:</span>
                        <span className="font-medium">{memberDetails?.timezone || 'Not provided'}</span>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Account Status */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Shield className="h-5 w-5" />
                        Account Status
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Status:</span>
                        <Badge className={`${getStatusColor(memberDetails?.status)} bg-opacity-10`}>
                          {memberDetails?.status || 'active'}
                        </Badge>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Member Since:</span>
                        <span className="font-medium">
                          {new Date(memberDetails?.registration_date).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Last Login:</span>
                        <span className="font-medium">
                          {memberDetails?.last_login ? new Date(memberDetails.last_login).toLocaleDateString() : 'Never'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Tron Wallet:</span>
                        <span className="font-mono text-xs">
                          {memberDetails?.tron_wallet ? 
                            `${memberDetails.tron_wallet.slice(0, 10)}...${memberDetails.tron_wallet.slice(-6)}` : 
                            'Not provided'
                          }
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Network Stats */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="p-4 text-center">
                      <TrendingUp className="h-8 w-8 text-green-600 mx-auto mb-2" />
                      <div className="text-2xl font-bold text-gray-900">
                        ${memberDetails?.total_earnings || 0}
                      </div>
                      <div className="text-sm text-gray-600">Total Earnings</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 text-center">
                      <Users className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                      <div className="text-2xl font-bold text-gray-900">
                        {memberDetails?.total_referrals || 0}
                      </div>
                      <div className="text-sm text-gray-600">Total Referrals</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 text-center">
                      <CheckCircle className="h-8 w-8 text-purple-600 mx-auto mb-2" />
                      <div className="text-2xl font-bold text-gray-900">
                        {memberDetails?.pif_sponsored || 0}
                      </div>
                      <div className="text-sm text-gray-600">PIF Sponsored</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 text-center">
                      <Eye className="h-8 w-8 text-orange-600 mx-auto mb-2" />
                      <div className="text-2xl font-bold text-gray-900">
                        {memberDetails?.lead_capture_pages || 0}
                      </div>
                      <div className="text-sm text-gray-600">Lead Pages</div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              <TabsContent value="financial" className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <CreditCard className="h-5 w-5" />
                        Earnings Summary
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total Earnings:</span>
                        <span className="font-bold text-green-600">
                          ${memberDetails?.total_earnings?.toFixed(2) || '0.00'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">This Month:</span>
                        <span className="font-medium">$45.50</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Last Payout:</span>
                        <span className="font-medium">$23.80 (Sep 28)</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Pending:</span>
                        <span className="font-medium">$11.90</span>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <TrendingUp className="h-5 w-5" />
                        Performance Metrics
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Conversion Rate:</span>
                        <span className="font-medium">12.5%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Avg. Commission:</span>
                        <span className="font-medium">$5.95</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Active Campaigns:</span>
                        <span className="font-medium">{memberDetails?.active_campaigns || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Phase Status:</span>
                        <Badge className="bg-yellow-100 text-yellow-800">
                          Phase 1 (Need 9 more PIF)
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              <TabsContent value="activity" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Calendar className="h-5 w-5" />
                      Recent Activity
                    </CardTitle>
                    <CardDescription>
                      Latest member actions and system events
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {memberDetails?.network_activity?.map((activity, index) => (
                        <div key={index} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                          <div className={`w-2 h-2 rounded-full mt-2 ${
                            activity.type === 'referral' ? 'bg-green-500' :
                            activity.type === 'pif' ? 'bg-blue-500' : 'bg-gray-400'
                          }`}></div>
                          <div className="flex-1">
                            <p className="text-sm text-gray-900">{activity.description}</p>
                            <p className="text-xs text-gray-500">{new Date(activity.date).toLocaleDateString()}</p>
                          </div>
                        </div>
                      )) || (
                        <div className="text-center text-gray-500 py-4">
                          No recent activity found
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="support" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <MessageSquare className="h-5 w-5" />
                      Support Tickets
                    </CardTitle>
                    <CardDescription>
                      Member support history and open tickets
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {memberDetails?.support_tickets?.map((ticket) => (
                        <div key={ticket.id} className="flex items-center justify-between p-3 border rounded-lg">
                          <div>
                            <div className="font-medium text-gray-900">#{ticket.id}: {ticket.subject}</div>
                            <div className="text-sm text-gray-500">{new Date(ticket.date).toLocaleDateString()}</div>
                          </div>
                          <Badge className={
                            ticket.status === 'resolved' ? 'bg-green-100 text-green-800' :
                            ticket.status === 'open' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }>
                            {ticket.status}
                          </Badge>
                        </div>
                      )) || (
                        <div className="text-center text-gray-500 py-4">
                          No support tickets found
                        </div>
                      )}
                    </div>
                    
                    <div className="mt-6 pt-6 border-t">
                      <Button className="w-full" onClick={() => toast.info('Support message feature coming soon')}>
                        <MessageSquare className="h-4 w-4 mr-2" />
                        Send Message to Member
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          )}
        </div>

        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-gray-50 border-t p-6">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Member ID: {memberDetails?.id || member.id}
            </div>
            <div className="flex space-x-3">
              <Button variant="outline" onClick={() => toast.info('Lead capture audit feature coming soon')}>
                <Eye className="h-4 w-4 mr-2" />
                View Lead Captures
              </Button>
              <Button variant="outline" onClick={() => toast.info('Compliance tools coming soon')}>
                <Shield className="h-4 w-4 mr-2" />
                Compliance Check
              </Button>
              <Button onClick={onClose} className="btn-primary">
                Close
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemberCRMModal;